// How many rows a list shows at a time. It rides in the address as `pageSize`,
// like the filters, so the route asks the API for it and a link opens the list
// the same; and the last choice per list is remembered in this browser, since
// how many rows fit is a matter of the screen you sit at, not of the account.

export const PAGE_SIZES = [25, 50, 100, 200] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

const storageKey = (table: string) => `avds:pageSize:${table}`;

/** A page size this list offers, or null for anything else. */
export function pageSizeOf(value: unknown): PageSize | null {
  const size = Number(value);
  return (PAGE_SIZES as readonly number[]).includes(size) ? (size as PageSize) : null;
}

export function rememberedPageSize(table: string, fallback: PageSize = DEFAULT_PAGE_SIZE): PageSize {
  try {
    return pageSizeOf(window.localStorage.getItem(storageKey(table))) ?? fallback;
  } catch {
    return fallback;
  }
}

export function rememberPageSize(table: string, size: PageSize): void {
  try {
    window.localStorage.setItem(storageKey(table), String(size));
  } catch {
    /* a private window: the choice holds for this visit, through the address */
  }
}

/** The page size a list asks the API for: the one in the address, else the one remembered here. */
export function listPageSize(search: { pageSize?: number }, table: string, fallback: PageSize = DEFAULT_PAGE_SIZE): PageSize {
  return pageSizeOf(search.pageSize) ?? rememberedPageSize(table, fallback);
}
